import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import GroupPlan from "@/components/study/GroupPlan";
import { BookOpen } from "lucide-react";

/** El profesor abre su clase y ve por dónde va el grupo. */
export default function ClassPlan() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");

  useEffect(() => {
    api.get("/groups").then((r) => {
      const activos = (r.data || []).filter((g) => !g.archived_at);
      setGroups(activos);
      setGroupId((prev) => prev || activos[0]?.id || "");
    }).catch(() => setGroups([]));
  }, []);

  const conNivel = useMemo(() => groups.filter((g) => g.level), [groups]);
  const sinNivel = useMemo(() => groups.filter((g) => !g.level), [groups]);
  const puedeMarcar = ["teacher", "superadmin", "academic_director"].includes(user?.role);

  if (user && !user.study_plan_access) {
    return (
      <Card className="p-10 text-center" data-testid="class-plan-locked">
        <BookOpen className="h-7 w-7 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-600 font-medium">
          El plan de estudio todavía no está habilitado para tu cuenta
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="class-plan-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-brand-blue" /> Plan de estudio de mi clase
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Dónde quedó el grupo, qué sigue y el material de cada unidad.
        </p>
      </div>

      <div className="max-w-sm">
        <Select value={groupId} onValueChange={setGroupId}>
          <SelectTrigger data-testid="class-plan-group-select">
            <SelectValue placeholder="Elegí una clase" />
          </SelectTrigger>
          <SelectContent>
            {conNivel.map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.name} · {g.level}</SelectItem>
            ))}
            {sinNivel.map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.name} · sin nivel</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {groupId
        ? <GroupPlan key={groupId} groupId={groupId} canMark={puedeMarcar} />
        : <Card className="p-10 text-center text-slate-500" data-testid="class-plan-pick-hint">
            Elegí una clase para ver su plan
          </Card>}
    </div>
  );
}
